// The blog's front page: every published post, newest first — and for the
// admin, their drafts above them and a way to start another.
//
// Until the blog opens (`BLOG_OPEN`) this page is the admin's alone; to anyone
// else there is no blog.
import { BLOG_OPEN } from "@bibleunleashed/shared";
import { useEffect, useMemo } from "react";
import { Link, useSearch } from "wouter";
import { Header } from "@/components/Header";
import { PageText } from "@/components/PageText";
import { Icon } from "@/components/Icon";
import { TagList } from "@/components/TagList";
import { useUser } from "@/lib/auth";
import { postDate, tagCounts, usePosts } from "@/lib/posts";
import { Placeholder } from "./Placeholder";

const label = "mb-2 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase";

export function Blog() {
  const { data: user, isLoading: userLoading } = useUser();
  const isAdmin = user?.role === "admin";
  const { data: posts, isLoading, error } = usePosts("blog");
  const tag = new URLSearchParams(useSearch()).get("tag");

  useEffect(() => {
    document.title = "Blog · The Bible Unleashed";
  }, []);

  const tags = useMemo(() => tagCounts(posts ?? []), [posts]);
  const shown = (posts ?? []).filter((p) => !tag || p.tags.includes(tag));

  if (!BLOG_OPEN && !isAdmin) {
    return userLoading ? <Header /> : <Placeholder title="Page not found" />;
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-[1080px] px-6 py-14 lg:px-10">
        <p className="text-xs tracking-[0.12em] text-ink-3 uppercase">Blog</p>
        <PageText
          id="blog"
          titleClassName="mt-1.5 font-serif text-[40px] font-semibold tracking-[-0.015em]"
          bodyClassName="mt-2 max-w-[560px] font-serif text-[17px] leading-normal"
        />
        {!BLOG_OPEN && (
          <p className="mt-2 font-sans text-[13px] text-ink-2">
            The blog is not open to readers yet, so only you can see this page and its posts.
          </p>
        )}

        <div className="mt-10 grid gap-16 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div>
            {tag && (
              <p className="mb-4 font-sans text-[13px] text-ink-2">
                Tagged “{tag}” ·{" "}
                <Link href="/blog" className="text-accent">
                  every post
                </Link>
              </p>
            )}
            {isLoading && <p className="font-serif text-ink-3">Loading…</p>}
            {error && <p className="font-serif text-ink-2">Couldn’t load the posts: {error.message}</p>}
            {posts && shown.length === 0 && <p className="font-serif text-ink-3">Nothing published yet.</p>}

            {shown.map((p) => (
              <article
                key={p.id}
                className="grid gap-2 border-t border-rule py-6 last:border-b sm:grid-cols-[110px_minmax(0,1fr)] sm:gap-6"
              >
                <div className="pt-1.5 font-sans text-[12.5px] text-ink-3 tabular-nums">
                  {p.publishedAt ? postDate(p.publishedAt) : "Draft"}
                </div>
                <div className="min-w-0">
                  <h2 className="font-serif text-[24px] leading-[1.2] font-semibold tracking-[-0.01em]">
                    <Link href={`/blog/${p.slug}`} className="text-ink hover:text-accent">
                      {p.title}
                    </Link>
                  </h2>
                  {p.summary && <p className="mt-2 font-serif text-[16.5px] leading-[1.55] text-ink-2">{p.summary}</p>}
                  {p.tags.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {p.tags.map((t) => (
                        <Link
                          key={t}
                          href={`/blog?tag=${encodeURIComponent(t)}`}
                          className="rounded-full border border-rule px-2.5 py-0.5 font-sans text-[11.5px] text-ink-3 hover:text-ink"
                        >
                          {t}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>

          <aside className="space-y-8">
            {isAdmin && (
              <Link
                href="/blog/new"
                className="flex items-center justify-center gap-1.5 rounded-md bg-accent px-3 py-2 text-[13px] font-medium text-white"
              >
                <Icon name="plus" width="15" height="15" /> New post
              </Link>
            )}
            <TagList title="Tags" tags={tags} current={tag} href={(t) => `/blog?tag=${encodeURIComponent(t)}`} />
            <div>
              <p className={label}>Follow along</p>
              <a href="/rss.xml" className="font-sans text-[13.5px] text-ink-2 hover:text-ink">
                RSS — new posts and chapters
              </a>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
